<template>
    <div
        :class="componentClasses"
        @click="onClickComponent"
        @keydown.enter="onClickComponent"
    >
        <div
            v-if="showGlare"
            class="glare"
            :style="{ animationDuration: `${repeatAfterSeconds}s` }"
        />
        <div class="floating-beam-minimized--label">
            {{ assistent?.widget.beamReopenLabel }}
        </div>
        <div class="floating-beam-minimized--arrow-icon">
            <angle-down-icon v-if="floatingBeamPositionTop" />
            <angle-up-icon v-else />
        </div>
    </div>
</template>

<script setup lang="ts">
    import useConfiguration from '@/composables/useConfiguration';
    import AngleUpIcon from '@keyvue/one-awesome-icons/icons/regular/angle-up.svg?component';
    import AngleDownIcon from '@keyvue/one-awesome-icons/icons/regular/angle-down.svg?component';
    import { computed, onMounted, onUnmounted, ref } from 'vue';
    import { Position } from '@keyvue/one-api-client';
    import useWidgetAppearance from '@/composables/useWidgetAppearance';

    const { assistent } = useConfiguration();
    const { positionForIcon } = useWidgetAppearance();

    const showFloatingBeam = ref(true);
    const showGlare = ref(false);

    const hintAfterSeconds = assistent.value?.widget.hintAfterSeconds ?? 0;
    const repeatAfterSeconds = assistent.value?.widget.repeatAfterSeconds ?? 0;

    const emit = defineEmits<{
        (e: 'click'): void;
    }>();

    function onClickComponent() {
        emit('click');
    }

    const componentClasses = computed(() => {
        return {
            'floating-beam-minimized': true,
            'floating-beam-minimized--top': floatingBeamPositionTop.value,
            'floating-beam-minimized--bottom': floatingBeamPositionBottom.value,
            'glare': true,
        };
    });

    const checkScrollPosition = () => {
        if (floatingBeamPositionTop.value) {
            const scrollPercentage = window.scrollY / document.documentElement.scrollHeight;

            const widget = assistent.value?.widget;

            if (widget && widget.beamDisplayAfterPercentage >= 1) {
                const multiplier = widget.beamDisplayAfterPercentage / 100;
                showFloatingBeam.value = scrollPercentage >= multiplier;
            } else {
                showFloatingBeam.value = true;
            }
        } else {
            showFloatingBeam.value = true;
        }
    };

    const floatingBeamPositionTop = computed(() => {
        if (!positionForIcon.value) {
            return false;
        }

        return Position.TOP_LEFT.equals(positionForIcon.value) || Position.TOP_RIGHT.equals(positionForIcon.value);
    });

    const floatingBeamPositionBottom = computed(() => {
        if (!positionForIcon.value) {
            return false;
        }

        return Position.BOTTOM_LEFT.equals(positionForIcon.value) || Position.BOTTOM_RIGHT.equals(positionForIcon.value);
    });

    onMounted(() => {
        if (hintAfterSeconds > 0) {
            setTimeout(() => {
                showGlare.value = true;
                if (repeatAfterSeconds > 0) {
                    setInterval(() => {
                        showGlare.value = !showGlare.value;
                    }, repeatAfterSeconds * 1000);
                }
            }, hintAfterSeconds * 1000);
        }
    });

    onMounted(() => {
        window.addEventListener('scroll', checkScrollPosition);
    });

    onUnmounted(() => {
        window.removeEventListener('scroll', checkScrollPosition);
    });
</script>

<style scoped lang="scss">
.floating-beam-minimized {
    width: min-content;
    background-color: var(--color-widget);
    pointer-events: auto;

    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    align-content: center;
    gap: .5rem;
    padding: .25rem 1rem;

    margin-left: auto;
    margin-right: auto;
    position: relative;
    overflow: hidden;

    cursor: pointer;
    user-select: none;
    transition: transform 200ms ease;

    &:hover {
        transform: scale(1.05);
    }

    &--top {
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
    }

    &--bottom {
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
    }

    &--label {
        color: var(--color-widget-text);
        font-size: 16px;
        text-wrap: nowrap;
    }

    &--arrow-icon {
        width: 18px;
        height: 18px;

        & > svg {
            color: white;
            width: 18px;
            height: 18px;
        }
    }

    .glare {
        position: absolute;
        top: -20%;
        left: -100%;
        width: 150%;
        height: 200%;
        background: linear-gradient(145deg,
            rgba(255, 255, 255, 0) 0%,
            color-mix(in srgb, var(--color-widget) 60%, white) 45%,
            color-mix(in srgb, var(--color-widget) 85%, white) 55%,
            rgba(255, 255, 255, 0) 100%);
        animation: shine infinite linear;
        opacity: 0.8;
    }

    @keyframes shine {
        0% {
            transform: translate(-100%, -10%);
        }
        100% {
            transform: translate(200%, -10%);
        }
    }
}
</style>
